/**
 * A markdown link that names a project document (RFC 0297 §5a).
 *
 * `[the canvas](3nHaHvn…)` in a document's prose is an href with no scheme, so
 * react-markdown renders a plain anchor and the browser resolves it RELATIVE TO
 * THE ROUTE: from `/<worldId>/<projectId>` a bare document id navigates to
 * `/<worldId>/<documentId>`, which the router reads as a project that does not
 * exist.
 * The link was not merely inert. It lost your place.
 *
 * So an href is resolved against the catalog before it is rendered. A hit is a
 * chip that opens the document in a pane; a lone link in its own block is the
 * document itself, drawn read-only. A miss is left exactly as it was, because
 * an ordinary URL is an ordinary link and this file has no opinion about it.
 *
 * RESOLUTION IS THE SAME ONE THE ROUTER USES. `resolveSubject` already answers
 * "what does this id open as", including the case that matters here: an app
 * INSTANCE resolves to its own id plus the view that draws it, so a canvas
 * embeds as a canvas rather than as the JSON underneath it.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo'
import type { ProjectDoc } from '@mod/core/model'
import { FileText } from 'lucide-react'
import {
  type ComponentType,
  createContext,
  lazy,
  type MouseEvent,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useDocument } from '../../services/world/repo/useDocument'
import { readWorldPointer } from '../../services/world/world'
import { ErrorBoundary } from '../../shell/window/ErrorBoundary'
import {
  catalogOf,
  type ProjectCatalog,
  resolveSubject,
} from '../../shell/window/resolveSubject'
import type { Subject } from '../../shell/window/subject'

/**
 * Imported LAZILY to break a module cycle, not to save bytes.
 *
 * `DocumentView` imports `MarkdownReader`, which imports this file, which needs
 * `DocumentView` to draw an embed. A static import closes that loop at module
 * evaluation and the bundler resolves one of the three to `undefined`. A
 * dynamic import defers the edge to render time, when all three exist.
 */
const LazyDocumentView = lazy(async () => {
  const mod = (await import('./DocumentView')) as {
    DocumentView: ComponentType<{ file: Subject; projectId: string }>
  }
  return { default: mod.DocumentView }
})

/**
 * Are we already inside an embed?
 *
 * ONE LEVEL, and the reason is not taste. A markdown document embedding a
 * markdown document that embeds the first is an infinite render, and a chain
 * three deep mounts three app bridges to show something nobody can read at that
 * size. Inside an embed every document link is a chip.
 */
const Embedded = createContext(false)

export function EmbeddedScope({ children }: { children: ReactNode }) {
  return <Embedded.Provider value={true}>{children}</Embedded.Provider>
}

export const useEmbedded = () => useContext(Embedded)

/** Where a link asks the shell to put what it opens. Plain clicks retain the
 * normal reuse/reveal rules; command-click is an explicit split, and adding
 * shift moves the subject into a fresh space. */
export type LinkOpenTarget = 'current' | 'split' | 'space'

export function linkOpenTarget(
  event: Pick<MouseEvent, 'metaKey' | 'shiftKey'>,
): LinkOpenTarget {
  if (!event.metaKey) return 'current'
  return event.shiftKey ? 'space' : 'split'
}

/** An href the browser owns: a scheme, a protocol-relative host, a fragment. */
const EXTERNAL = /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i

/**
 * Turn an href into what it opens, or null if it names nothing here.
 *
 * BY ID FIRST, THEN BY PATH. An agent writes the id because that is what
 * `create_document` hands back; a person writes the path because that is what
 * the sidebar shows them. Both are how documents are actually named in this
 * project, and supporting only the first would make the feature work for
 * whoever wrote the link and not for whoever edits it later.
 */
export function resolveHref(
  catalog: ProjectCatalog | null,
  href: string | undefined,
): Subject | null {
  if (!href || !catalog || EXTERNAL.test(href)) return null
  // Web pages are OFF here even where a pane would allow them: a link in prose
  // that happens to match a web subject id is a link, and embedding the open
  // internet in a document is a decision nobody made by typing a URL.
  const opts = { webFileFromId: () => null, canEmbedWeb: false }
  const byId = resolveSubject(catalog, href, opts)
  if (byId) return byId
  const byPath = catalog.documents.find((d) => d.path === href)
  return byPath ? resolveSubject(catalog, byPath.id, opts) : null
}

/** `resolveHref` bound to the project this pane is showing. */
export function useDocumentResolver(projectId: string) {
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const worldId = readWorldPointer() ?? undefined
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  // Not until the World has loaded, when there is one: without its apps a
  // record of a World app would read as having none (RFC 0338 S7).
  const catalog = useMemo(
    () =>
      projectDoc && (!worldId || worldDoc)
        ? catalogOf(projectDoc as ProjectDoc, worldDoc)
        : null,
    [projectDoc, worldDoc, worldId],
  )
  return useCallback(
    (href: string | undefined) => resolveHref(catalog, href),
    [catalog],
  )
}

/**
 * A document named inside a sentence.
 *
 * A button rather than an anchor, because there is no URL that means "this
 * document in a pane beside the one you are reading". Styled as a link anyway:
 * it sits in prose and reads as prose, and the icon is what says it leads
 * somewhere inside the project rather than out of it.
 */
export function DocumentChip({
  subject,
  onOpen,
  children,
}: {
  subject: Subject
  onOpen?: (id: string, target?: LinkOpenTarget) => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={(event) => onOpen?.(subject.id, linkOpenTarget(event))}
      title={subject.path}
      className="inline items-baseline gap-1 rounded align-baseline text-foreground underline decoration-border decoration-1 underline-offset-2 transition-colors hover:decoration-foreground/60"
    >
      <FileText
        className="mr-1 inline size-3.5 shrink-0 translate-y-[-1px] text-muted-foreground"
        aria-hidden
      />
      {children}
    </button>
  )
}

/** How tall an embed stands. Enough to read a canvas frame, short enough that
 *  the prose around it still reads as the document you opened. */
const EMBED_HEIGHT = 420

/** One quiet line where the preview would be: loading, or unable to draw. */
function EmbedNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center text-muted-foreground/60 text-xs">
      {children}
    </div>
  )
}

/**
 * A document named on a line of its own, drawn as itself.
 *
 * INERT UNTIL YOU CLICK IT, which is the trade a canvas forces. A canvas takes
 * the wheel to zoom, so an embed that was live from the start would be a scroll
 * trap in the middle of a page: you scroll toward it and the document stops
 * moving while the picture zooms. Live from the start is also what you want the
 * moment you have decided to look at it, so the click that used to open the
 * document now wakes it, and Open in the caption is what opens it.
 *
 * The same shape every embedded map uses, for the same reason.
 *
 * NO `onSubject`. An app view announces what it is showing so the host can
 * scope the comment column to it, which is right for a pane and wrong here: an
 * embedded canvas would silently re-point the column away from the document a
 * person is actually reading. Omitting the callback makes the announcement a
 * no-op rather than a hijack.
 */
export function DocumentEmbed({
  subject,
  projectId,
  onOpen,
}: {
  subject: Subject
  projectId: string
  onOpen?: (id: string, target?: LinkOpenTarget) => void
}) {
  const [live, setLive] = useState(false)
  return (
    <figure className="group my-5 overflow-hidden rounded-[var(--radius-project)] border border-border bg-background">
      <figcaption className="flex items-center gap-2 border-border/60 border-b bg-muted/30 px-3 py-2">
        <FileText
          className="size-3.5 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-foreground text-xs">
          {subject.title}
        </span>
        {live ? (
          <button
            type="button"
            onClick={() => setLive(false)}
            className="shrink-0 rounded-md px-2 py-0.5 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
          >
            Release
          </button>
        ) : null}
        <button
          type="button"
          onClick={(event) => onOpen?.(subject.id, linkOpenTarget(event))}
          className="shrink-0 rounded-md px-2 py-0.5 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
        >
          Open
        </button>
      </figcaption>
      <div className="relative" style={{ height: EMBED_HEIGHT }}>
        <div
          className={`absolute inset-0 flex min-h-0 flex-col overflow-hidden ${
            live ? '' : 'pointer-events-none'
          }`}
        >
          {/* A PANE THAT FAILS IS ONE PANE; AN EMBED THAT FAILS IS THE PAGE.
              Without this, anything `DocumentView` throws on propagates to the
              root and blanks the document a person was reading, because the
              thing that broke is inside their prose rather than beside it. The
              caption and Open survive the fallback, so a preview that cannot
              draw is still a way in. */}
          {/* A record whose app is not installed here (RFC 0338 S7) has no
              view to preview; Open goes to the pane that offers to install it. */}
          {subject.missingApp ? (
            <EmbedNote>
              No app here draws this {subject.missingApp} record. Open it to
              install one.
            </EmbedNote>
          ) : (
            <ErrorBoundary
              fallback={<EmbedNote>Cannot preview this here.</EmbedNote>}
            >
              <Suspense
                fallback={<EmbedNote>Loading {subject.title}…</EmbedNote>}
              >
                <EmbeddedScope>
                  <LazyDocumentView file={subject} projectId={projectId} />
                </EmbeddedScope>
              </Suspense>
            </ErrorBoundary>
          )}
        </div>
        {live ? null : (
          <button
            type="button"
            onClick={() => setLive(true)}
            aria-label={`Interact with ${subject.title}`}
            className="absolute inset-0 flex cursor-pointer items-end justify-center pb-3"
          >
            {/* Only on hover: a permanent label over every preview in a
                document is furniture, and the pointer already changes. */}
            <span className="rounded-full border border-border bg-background/90 px-2.5 py-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
              Click to zoom and pan
            </span>
          </button>
        )}
      </div>
    </figure>
  )
}

/**
 * The href of the ONLY thing in a block, when that thing is a link.
 *
 * Reading the hast node rather than the rendered children, because by the time
 * react-markdown hands over children they are React elements and asking "is
 * this one of mine" means comparing component identities. The node still says
 * `a`.
 *
 * Whitespace between the link and the block's edges is ignored: markdown puts a
 * text node there often enough that requiring exactly one child would make the
 * rule fire at random.
 */
export function loneLinkHref(node: unknown): string | undefined {
  const el = node as
    | {
        children?: Array<{
          type?: string
          tagName?: string
          value?: string
          properties?: Record<string, unknown>
        }>
      }
    | undefined
  const kids = (el?.children ?? []).filter(
    (c) => !(c.type === 'text' && !String(c.value ?? '').trim()),
  )
  if (kids.length !== 1) return undefined
  const only = kids[0]
  if (only.type !== 'element' || only.tagName !== 'a') return undefined
  const href = only.properties?.href
  return typeof href === 'string' ? href : undefined
}
